import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Headset, X, Minus, Send } from "lucide-react";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 6000;
const STORAGE_KEY = "gc_support_session";

// Lumora brand palette — keeps the widget cohesive with the AI chat.
const C = {
  ink: "#0b0d17",
  primary: "#346af8",
  primary2: "#5b8bff",
  canvas: "#f1f4ff",
  border: "#d9e0f5",
  muted: "#7f88a8",
};

export default function SupportChat() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(
    () => localStorage.getItem(STORAGE_KEY) || null
  );
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [status, setStatus] = useState({ business_open: false, next_open_phrase: "" });
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  // Fetch business-hours status whenever we open the widget
  useEffect(() => {
    if (!open) return;
    api.get("/support/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [open]);

  // Listen for global "open human support" events from Navbar / LEO
  useEffect(() => {
    const openHandler = (e) => {
      // Close LEO if it's open — both widgets share the bottom-right corner
      window.dispatchEvent(new CustomEvent("gc:close-leo"));
      // If LEO handed off an existing session, adopt it so we keep one single
      // conversation (and don't spin up a second session that rings forever).
      const adopt = e?.detail?.sessionId;
      if (adopt) {
        setSessionId(adopt);
        localStorage.setItem(STORAGE_KEY, adopt);
      }
      setOpen(true);
      setPendingOpen(true);
    };
    const closeHandler = () => setOpen(false);
    window.addEventListener("gc:open-human-support", openHandler);
    window.addEventListener("gc:close-support", closeHandler);
    return () => {
      window.removeEventListener("gc:open-human-support", openHandler);
      window.removeEventListener("gc:close-support", closeHandler);
    };
  }, []);

  // Start a fresh session when the widget opens for the first time
  useEffect(() => {
    if (open && !sessionId) {
      let body = {};
      try {
        const stored = localStorage.getItem("gc_customer");
        if (stored) {
          const c = JSON.parse(stored);
          if (c?.email) body.email = c.email;
          if (c?.name) body.name = c.name;
        }
      } catch {}
      api.post("/support/start", body).then((r) => {
        setSessionId(r.data.session_id);
        localStorage.setItem(STORAGE_KEY, r.data.session_id);
      });
    }
  }, [open, sessionId]);

  // Hydrate history when chat opens with an existing session
  useEffect(() => {
    if (sessionId && open) {
      api.get(`/support/${sessionId}/messages`).then((r) => setMessages(r.data));
    }
  }, [sessionId, open]);

  // Poll for new messages — fetch the full list so the server is the single
  // source of truth (avoids the optimistic-echo duplication). Keep any
  // not-yet-confirmed optimistic messages until the server has them.
  useEffect(() => {
    if (!open || !sessionId) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/support/${sessionId}/messages`);
        if (Array.isArray(r.data)) {
          setMessages((prev) => {
            const server = r.data;
            const pendingKept = prev.filter(
              (m) =>
                m._optimistic &&
                !server.some((s) => s.role === "user" && s.text === m.text)
            );
            return [...server, ...pendingKept];
          });
        }
      } catch {}
    }, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [open, sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  // Clear the pending flag once we actually opened
  useEffect(() => {
    if (open && pendingOpen) setPendingOpen(false);
  }, [open, pendingOpen]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || !sessionId || sending) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { role: "user", text, timestamp: new Date().toISOString(), _optimistic: true },
    ]);
    setSending(true);
    try {
      await api.post("/support/message", { session_id: sessionId, text });
    } catch (err) {
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          system: true,
          text: "We couldn't send that just now. Please try again in a moment.",
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const endChat = () => {
    setOpen(false);
    setMessages([]);
    setSessionId(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.2, 0.7, 0.2, 1] }}
            data-testid="support-chat-window"
            className="fixed bottom-[88px] right-3 md:bottom-24 md:right-6 z-50 w-[calc(100%-24px)] md:w-[400px] h-[520px] md:h-[580px] max-h-[78vh] bg-canvas border border-warm rounded-3xl soft-shadow-lg flex flex-col overflow-hidden"
          >
            <div className="px-5 py-4 flex items-center gap-3" style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, color: "#fff" }}>
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(255,255,255,0.18)", border: "1px solid rgba(255,255,255,0.28)" }}>
                <Headset className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold leading-tight">
                  GC SPARK Support
                </div>
                <div
                  data-testid="support-chat-status"
                  className="text-xs inline-flex items-center gap-1.5"
                  style={{ color: "rgba(255,255,255,0.85)" }}
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full ${status.business_open ? "animate-pulse" : ""}`}
                    style={{ background: status.business_open ? "#4ade80" : "#fbbf24" }}
                  />
                  {status.business_open
                    ? "Live human team · typical reply in minutes"
                    : `Team offline · we reply ${status.next_open_phrase || "next business day"}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Minimise support chat"
                data-testid="support-chat-minimise"
                className="h-9 w-9 rounded-full flex items-center justify-center transition-colors"
                style={{ color: "#fff" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.16)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0)")}
              >
                <Minus className="w-5 h-5" />
              </button>
              <button
                type="button"
                onClick={endChat}
                aria-label="End support chat"
                data-testid="support-chat-close"
                className="h-9 w-9 -mr-1 rounded-full flex items-center justify-center transition-colors"
                style={{ color: "#fff" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.16)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0)")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" style={{ background: C.canvas }}>
              {messages.map((m, i) => {
                const isCustomer = m.role === "user";
                const isManager = m.role === "manager";
                const bubbleStyle = isCustomer
                  ? { background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)`, color: "#fff", borderBottomRightRadius: 6 }
                  : isManager
                  ? { background: "rgba(91,87,224,0.10)", color: C.ink, border: "1px solid rgba(91,87,224,0.28)", borderBottomLeftRadius: 6 }
                  : m.system
                  ? { background: "rgba(91,87,224,0.06)", color: C.ink, border: `1px solid ${C.border}`, borderBottomLeftRadius: 6 }
                  : { background: "#fff", color: C.ink, border: `1px solid ${C.border}`, borderBottomLeftRadius: 6 };
                return (
                  <div
                    key={i}
                    className={`flex ${isCustomer ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      data-testid={`support-bubble-${m.role}-${i}`}
                      className="max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                      style={{ ...bubbleStyle, boxShadow: isCustomer ? "0 6px 18px -10px rgba(41,45,85,0.45)" : "none" }}
                    >
                      {m.text}
                    </div>
                  </div>
                );
              })}
              {sending && (
                <div className="flex justify-end">
                  <div className="rounded-2xl rounded-br-sm px-4 py-3 flex items-center gap-1.5" style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)` }}>
                    <span className="typing-dot" style={{ background: "#fff" }} />
                    <span className="typing-dot" style={{ background: "#fff" }} />
                    <span className="typing-dot" style={{ background: "#fff" }} />
                  </div>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            <form
              onSubmit={send}
              className="border-t p-3 flex items-center gap-2 bg-white"
              style={{ borderColor: C.border }}
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message…"
                data-testid="support-chat-input"
                className="flex-1 h-11 px-4 rounded-full text-sm focus:outline-none"
                style={{ border: `1px solid ${C.border}`, color: C.ink }}
                onFocus={(e) => (e.currentTarget.style.boxShadow = `0 0 0 3px ${C.primary}33`)}
                onBlur={(e) => (e.currentTarget.style.boxShadow = "none")}
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="support-chat-send"
                className="h-11 w-11 rounded-full text-white flex items-center justify-center disabled:opacity-50 transition-opacity hover:opacity-90"
                style={{ background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primary2} 100%)` }}
              >
                <Send className="w-4 h-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
